/**
 * PURE — import header aliasing for the wizard's Columns step.
 *
 * Ported from `phase1/import/header-aliases.ts`, but the canonical names are
 * **ExecBridge's**, not the reference's, because the mapped file is uploaded to
 * ExecBridge's importer. The one that bites: the reference calls it `uom`,
 * ExecBridge calls it `unit_of_measure`. The alias *spellings* are the union of
 * both apps' tables, so matching is as forgiving as the reference while the
 * output still satisfies `services/metrics_service.py::CSV_HEADER_ALIASES`.
 *
 * Keep in sync with that module — it is the server-side authority on what an
 * import will accept.
 */

/** The 19 fields ExecBridge's importer understands after alias resolution. */
export type CanonicalField =
  | "account_id"
  | "account_name"
  | "business_impact"
  | "category"
  | "category_id"
  | "description"
  | "format"
  | "formula_compute"
  | "kpi_status"
  | "measurement_date"
  | "metric_id"
  | "metric_name"
  | "scale"
  | "source_account_id"
  | "source_account_name"
  | "source_system"
  | "subcategory"
  | "unit_of_measure"
  | "value";

/** Without these an import cannot proceed (`REQUIRED_CSV_COLUMNS`). */
export const REQUIRED_FIELDS: CanonicalField[] = [
  "account_name",
  "metric_id",
  "metric_name",
  "category",
  "value",
];

export const HEADER_ALIASES: Record<CanonicalField, readonly string[]> = {
  account_name: [
    "account_name",
    "account",
    "customer",
    "customer_name",
    "client",
    "client_name",
  ],
  account_id: ["account_id", "accountid", "internal_account_id"],
  source_account_name: ["source_account_name", "src_account_name"],
  source_account_id: [
    "source_account_id",
    "src_account_id",
    "external_account_id",
    "crm_account_id",
  ],
  metric_id: ["metric_id", "metric_ids", "metrics_id", "kpi_id", "kpi_ids"],
  metric_name: [
    "metric_name",
    "metric_names",
    "metric",
    "metrics",
    "kpi_name",
    "kpi_names",
    "kpi",
    "kpis",
    "name",
  ],
  measurement_date: [
    "measurement_date",
    "date",
    "as_of_date",
    "as_of",
    "reported_at",
    "reporting_date",
    "period_date",
    "measure_date",
    "actual_measure_date",
    // Customer exports label the date after the metric ("Metric Date").
    "metric_date",
    "metrics_date",
    "kpi_date",
  ],
  value: ["value", "metric_value", "kpi_value", "amount", "score"],
  unit_of_measure: ["unit_of_measure", "uom", "unit"],
  source_system: ["source_system", "system", "source"],
  formula_compute: ["formula_compute", "formula", "compute"],
  category: [
    "category",
    "categories",
    "metric_category",
    "metric_categories",
    "kpi_category",
    "kpi_categories",
  ],
  // The short id ("financial"), as opposed to `category`, the name. Imported
  // since 2026-09-29; before that the column was silently dropped.
  category_id: [
    "category_id",
    "category_ids",
    "cat_id",
    "category_code",
    "category_slug",
    "category_key",
  ],
  subcategory: [
    "subcategory",
    "subcategories",
    "sub_category",
    "sub_categories",
    "metric_subcategory",
  ],
  format: ["format", "value_format"],
  scale: ["scale", "value_scale"],
  description: [
    "description",
    "notes",
    "comment",
    "comments",
    "remark",
    "remarks",
    "note",
  ],
  kpi_status: ["kpi_status", "status"],
  business_impact: ["business_impact", "impact"],
};

/** Matches the reference's `normalize`: whitespace, `-`, `.` and `/` all fold to `_`. */
export function normalizeHeader(s: string): string {
  return (s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-./]+/g, "_");
}

const REVERSE_INDEX: Record<string, CanonicalField> = (() => {
  const idx: Record<string, CanonicalField> = {};
  (Object.keys(HEADER_ALIASES) as CanonicalField[]).forEach(canonical => {
    idx[normalizeHeader(canonical)] = canonical;
    HEADER_ALIASES[canonical].forEach(a => {
      // First alias wins, so an earlier field's claim is never silently stolen.
      if (!(normalizeHeader(a) in idx)) idx[normalizeHeader(a)] = canonical;
    });
  });
  return idx;
})();

/** Resolve one raw header to a canonical field, or "unknown". */
export function resolveHeader(raw: string): CanonicalField | "unknown" {
  if (!raw || !raw.trim()) return "unknown";
  return REVERSE_INDEX[normalizeHeader(raw)] ?? "unknown";
}

/** `ignore` means "present in the file, deliberately not imported". */
export type ColumnTarget = CanonicalField | "ignore";

export type ColumnMapping = {
  header: string;
  target: ColumnTarget;
  /** True when `resolveHeader` picked it, false once a human changed it. */
  auto: boolean;
};

/**
 * Propose a mapping for every header in the file.
 *
 * A canonical field is claimed at most once: if two headers resolve to the same
 * field, the first keeps it and the rest fall to `ignore`. Silently importing
 * two columns into one field would make whichever landed last win at random.
 */
export function proposeColumnMappings(headers: string[]): ColumnMapping[] {
  // Rank each header's claim on its field, so a contest is decided by how good
  // the match is rather than by column order. Position only breaks a tie.
  //
  // This is not cosmetic. A real file carried both "Actual Measure Date" and
  // "Measurement Date"; first-come gave the field to the sparse one, so most
  // rows shared a blank date, and the importer's dedupe on
  // (account, metric, date) collapsed 99 rows to 25.
  const rank = (header: string, field: CanonicalField): number => {
    const n = normalizeHeader(header);
    if (n === normalizeHeader(field)) return 0; // the canonical name itself
    const i = HEADER_ALIASES[field].findIndex(a => normalizeHeader(a) === n);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i + 1;
  };

  const bestFor = new Map<CanonicalField, { header: string; rank: number }>();
  headers.forEach((header, position) => {
    const resolved = resolveHeader(header);
    if (resolved === "unknown") return;
    const r = rank(header, resolved);
    const held = bestFor.get(resolved);
    if (!held || r < held.rank) {
      bestFor.set(resolved, { header, rank: r });
    }
    void position;
  });

  const winners = new Map<string, CanonicalField>();
  bestFor.forEach((v, field) => winners.set(v.header, field));

  return headers.map(header => {
    const field = winners.get(header);
    return field
      ? { header, target: field as ColumnTarget, auto: true }
      : { header, target: "ignore" as ColumnTarget, auto: true };
  });
}

export type ColumnDecisionCounts = {
  auto_matched: number;
  user_matched: number;
  ignored: number;
};

/** The counts the run summary and QC report display. */
export function countColumnDecisions(
  mappings: ColumnMapping[]
): ColumnDecisionCounts {
  let auto_matched = 0;
  let user_matched = 0;
  let ignored = 0;
  mappings.forEach(m => {
    if (m.target === "ignore") ignored++;
    else if (m.auto) auto_matched++;
    else user_matched++;
  });
  return { auto_matched, user_matched, ignored };
}

/** Required fields with no column mapped to them; blocks the step. */
export function missingRequiredFields(
  mappings: ColumnMapping[]
): CanonicalField[] {
  const mapped = new Set(mappings.map(m => m.target));
  return REQUIRED_FIELDS.filter(f => !mapped.has(f));
}

/** Fields mapped by more than one column; also blocks the step. */
export function duplicateTargets(mappings: ColumnMapping[]): CanonicalField[] {
  const seen = new Set<string>();
  const dupes = new Set<CanonicalField>();
  mappings.forEach(m => {
    if (m.target === "ignore") return;
    if (seen.has(m.target)) dupes.add(m.target);
    seen.add(m.target);
  });
  // Array.from, not spread: the repo targets ES5 without downlevelIteration.
  return Array.from(dupes);
}

/* ---------------- Template-driven resolution ---------------- */

/**
 * An organization's import template, from
 * `GET /metrics/datahub/import-template`.
 *
 * `from_fallback` is true when no template is configured and the server
 * returned the shipped format — surfaced so the console can say which it used
 * rather than leaving a user to wonder whether their configuration took effect.
 */
export type ImportTemplate = {
  aliases: Record<string, string[]>;
  high: string[];
  high_any_of: string[][];
  medium: string[];
  optional: string[];
  columns: { column_key: string; display_label?: string | null }[];
  from_fallback: boolean;
};

/** Every field a template accepts, in a stable order for the picker. */
export function templateFields(template?: ImportTemplate | null): string[] {
  if (!template) return [...CANONICAL_FIELD_LIST];
  const keys = template.columns?.length
    ? template.columns.map(c => c.column_key)
    : Object.keys(template.aliases ?? {});
  return Array.from(new Set(keys)).sort();
}

/** The hardcoded set, used whenever no template is available. */
export const CANONICAL_FIELD_LIST: string[] = (
  Object.keys(HEADER_ALIASES) as CanonicalField[]
).sort();

/** True when a field must be mapped for the import to proceed. */
export function isRequiredField(
  field: string,
  template?: ImportTemplate | null
): boolean {
  if (!template) return (REQUIRED_FIELDS as string[]).includes(field);
  if (template.high?.includes(field)) return true;
  return (template.high_any_of ?? []).some(group => group.includes(field));
}

/**
 * Resolve a header using a template's aliases.
 *
 * Falls back to the built-in table when the template does not recognise the
 * header, so configuring a template *adds* spellings rather than silently
 * removing the ones that already worked.
 */
export function resolveHeaderWithTemplate(
  raw: string,
  template?: ImportTemplate | null
): string {
  if (template?.aliases) {
    const n = normalizeHeader(raw);
    for (const [field, spellings] of Object.entries(template.aliases)) {
      if (normalizeHeader(field) === n || spellings.includes(n)) return field;
    }
  }
  return resolveHeader(raw);
}

/**
 * Propose a mapping for every header, honouring a template when present.
 *
 * Same one-field-one-column rule as `proposeColumnMappings`, and the same
 * tie-break: a header whose name *is* the field beats one that merely aliases
 * to it, so a file carrying both "Actual Measure Date" and "Measurement Date"
 * gives the field to the latter.
 */
export function proposeColumnMappingsFor(
  headers: string[],
  template?: ImportTemplate | null
): ColumnMapping[] {
  if (!template) return proposeColumnMappings(headers);

  const rank = (header: string, field: string): number => {
    const n = normalizeHeader(header);
    if (n === normalizeHeader(field)) return 0;
    const i = (template.aliases[field] ?? []).indexOf(n);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i + 1;
  };

  const bestFor = new Map<string, { header: string; rank: number }>();
  headers.forEach(header => {
    const field = resolveHeaderWithTemplate(header, template);
    if (field === "unknown") return;
    const r = rank(header, field);
    const held = bestFor.get(field);
    if (!held || r < held.rank) bestFor.set(field, { header, rank: r });
  });

  const winners = new Map<string, string>();
  bestFor.forEach((v, field) => winners.set(v.header, field));

  return headers.map(header => {
    const field = winners.get(header);
    return field
      ? { header, target: field as ColumnTarget, auto: true }
      : { header, target: "ignore" as ColumnTarget, auto: true };
  });
}

/** Requirements a mapping fails, as readable text. */
export function missingRequiredFor(
  mappings: ColumnMapping[],
  template?: ImportTemplate | null
): string[] {
  if (!template) return missingRequiredFields(mappings);
  const present = new Set(mappings.map(m => m.target));
  const missing = (template.high ?? []).filter(f => !present.has(f as never));
  (template.high_any_of ?? []).forEach(group => {
    if (!group.some(f => present.has(f as never)))
      missing.push(group.join(" or "));
  });
  return missing;
}
